import React, { useState, useEffect } from 'react';
import './styles.css';

function DataTable() {
  const [timeframe, setTimeframe] = useState("10m"); // Default timeframe set to 5m
  const [originalPricesData, setOriginalPricesData] = useState([]);
  const [pricesData, setPricesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortOrders, setSortOrders] = useState({
    avgHighPrice: "asc",
    avgLowPrice: "asc",
    margin: "asc",
    highPriceVolume: "asc",
    lowPriceVolume: "asc"
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [minLowPrice, setMinLowPrice] = useState('');
  const [maxLowPrice, setMaxLowPrice] = useState('');
  const [minMargin, setMinMargin] = useState('');
  const [minVolume, setMinVolume] = useState('');
  const [maxVolume, setMaxVolume] = useState('');
  const [mappingData, setMappingData] = useState([]);
  const [hideNoData, setHideNoData] = useState(false);
  const [visibleRows, setVisibleRows] = useState(15);

  const handleTimeframeChange = (newTimeframe) => {
    setTimeframe(newTimeframe === "latest" ? newTimeframe : `${newTimeframe}`);
  };

// Function to fetch mapping data
const fetchMappingData = async () => {
    try {
      const mappingResponse = await fetch("https://prices.runescape.wiki/api/v1/osrs/mapping");
      if (!mappingResponse.ok) {
        throw new Error('Failed to fetch mapping data');
      }
      const mappingData = await mappingResponse.json();
      setMappingData(mappingData);
      // Create a mapping of item IDs to their names
      const itemMapping = {};
      mappingData.forEach(item => {
        const { id, name } = item;
        itemMapping[id] = name;
      });
  
      return itemMapping;
    } catch (error) {
      console.error("Error fetching mapping data:", error);
      throw error; // Rethrow the error to handle it wherever this function is called
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        let url;
        let fiveMinVolumeData = {};
  
        if (timeframe === "latest") {
          url = "https://prices.runescape.wiki/api/v1/osrs/latest";
          await fetchMappingData(); // Fetch mapping data when timeframe is "latest"
  
          // Fetch the 5-minute data for volume
          const fiveMinResponse = await fetch("https://prices.runescape.wiki/api/v1/osrs/5m");
          if (!fiveMinResponse.ok) {
            throw new Error('Failed to fetch 5m data');
          }
          const fiveMinData = await fiveMinResponse.json();
          fiveMinVolumeData = fiveMinData.data;
        } else {
          url = `https://prices.runescape.wiki/api/v1/osrs/${timeframe}`;
        }
  
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch ${timeframe} data`);
        }
        const data = await response.json();
  
        let formattedPricesData = [];
        // Fetch mapping data for all timeframes
        const mappingResponse = await fetch("https://prices.runescape.wiki/api/v1/osrs/mapping");
        if (!mappingResponse.ok) {
          throw new Error('Failed to fetch mapping data');
        }
        const mappingData = await mappingResponse.json();
  
        // Create a mapping of item IDs to their names
        const itemMapping = {};
        mappingData.forEach(item => {
          const { id, name } = item;
          itemMapping[id] = name;
        });
  
        if (timeframe === "latest") {
          // Map each item ID to its corresponding object with itemName, high, and low
          formattedPricesData = Object.keys(data.data).map(itemId => ({
            itemName: itemMapping[itemId], // Use item name from mapping data
            avgHighPrice: data.data[itemId].high || "No Data",
            avgLowPrice: data.data[itemId].low || "No Data",
            highPriceVolume: fiveMinVolumeData[itemId]?.highPriceVolume || "No Data",
            lowPriceVolume: fiveMinVolumeData[itemId]?.lowPriceVolume || "No Data"
          }));
        } else {
          // Map each item ID to its corresponding object with itemName, avgHighPrice, avgLowPrice, highPriceVolume, and lowPriceVolume
          formattedPricesData = Object.keys(data.data).map(itemId => ({
            itemName: itemMapping[itemId], // Use item name from mapping data
            avgHighPrice: data.data[itemId].avgHighPrice || "No Data",
            avgLowPrice: data.data[itemId].avgLowPrice || "No Data",
            highPriceVolume: data.data[itemId].highPriceVolume || "No Data",
            lowPriceVolume: data.data[itemId].lowPriceVolume || "No Data"
          }));
        }
  
        setOriginalPricesData(formattedPricesData);
        setPricesData(formattedPricesData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error.message);
        setLoading(false);
      }
    };
  
    fetchData();
  }, [timeframe]);  

  const sortByColumn = (columnName) => {
    let sortedData;
    let newSortOrders = {};
  
    const parseValue = (value) => {
      return value === "No Data" ? 0 : parseFloat(value);
    };
  
    if (sortOrders[columnName] === "asc") {
      sortedData = [...formattedPricesData].sort((a, b) => {
        if (columnName === "itemName") {
          return a[columnName].localeCompare(b[columnName]);
        } else {
          return parseValue(a[columnName]) - parseValue(b[columnName]);
        }
      });
      newSortOrders = { ...sortOrders, [columnName]: "desc" };
    } else if (sortOrders[columnName] === "desc") {
      sortedData = [...formattedPricesData].sort((a, b) => {
        if (columnName === "itemName") {
          return b[columnName].localeCompare(a[columnName]);
        } else {
          return parseValue(b[columnName]) - parseValue(a[columnName]);
        }
      });
      newSortOrders = { ...sortOrders, [columnName]: "unsorted" };
    } else {
      sortedData = [...originalPricesData]; // Reset to the original unsorted data
      newSortOrders = { ...sortOrders, [columnName]: "asc" };
    }
  
    // Reset sorting orders for other columns
    Object.keys(newSortOrders).forEach(key => {
      if (key !== columnName) {
        newSortOrders[key] = "asc";
      }
    });
  
    setSortOrders(newSortOrders);
    setPricesData(sortedData);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  const calculateMargin = (highPrice, lowPrice) => {
    const high = highPrice === "No Data" ? 0 : highPrice;
    const low = lowPrice === "No Data" ? 0 : lowPrice;
    return high - low;
  };
  
  const calculateROI = (buyingPrice, profit) => {
    const price = buyingPrice === "No Data" ? 0 : parseFloat(buyingPrice);
    const profitValue = profit === "No Data" ? 0 : parseFloat(profit);
  
    if (price === 0) {
      return "0%";
    } else {
      return ((profitValue / price) * 100).toFixed(2) + "%";
    }
  };

  const formattedPricesData = pricesData.map(item => ({
    ...item,
    margin: calculateMargin(item.avgHighPrice === "No Data" ? 0 : item.avgHighPrice, item.avgLowPrice === "No Data" ? 0 : item.avgLowPrice),
    ROI: calculateROI(item.avgLowPrice === "No Data" ? 0 : item.avgLowPrice, item.margin)
  }));

  const handleFilterButtonClick = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const applyFilters = () => {
    const filteredData = originalPricesData.filter(item => {
      const lowPrice = parseFloat(item.avgLowPrice) || 0;
      const highPrice = parseFloat(item.avgHighPrice) || 0;
      const margin = highPrice - lowPrice;
      const volume = parseFloat(item.highPriceVolume) || 0;
  
      // Define filter conditions for each input field
      const lowPriceCondition = (minLowPrice === '' || lowPrice >= parseFloat(minLowPrice)) && (maxLowPrice === '' || lowPrice <= parseFloat(maxLowPrice));
      const marginCondition = (minMargin === '' || margin >= parseFloat(minMargin));
      const volumeCondition = (minVolume === '' || volume >= parseFloat(minVolume)) && (maxVolume === '' || volume <= parseFloat(maxVolume));
  
      const meetsFilterConditions = lowPriceCondition && marginCondition && volumeCondition;
      const shouldHideNoData = hideNoData && (item.avgHighPrice === "No Data" || item.avgLowPrice === "No Data");

      // Include the item in the filtered data only if it meets the filter conditions and should not be hidden
      return meetsFilterConditions && !shouldHideNoData;
    });
  
    setPricesData(filteredData);
  };

  const resetFilters = () => {
    setMinLowPrice("");
    setMaxLowPrice("");
    setMinMargin("");
    setMinVolume("");
    setMaxVolume("");
    if (minLowPrice === "" && maxLowPrice === "" && minMargin === "" && minVolume === "" && maxVolume === "") {
      setPricesData(originalPricesData);
    }
  };

  const handleShowMore = () => {
    setVisibleRows(prevVisibleRows => prevVisibleRows + 20);
  };

  return (
    <div className="ge-flipper">
        <div className="atheader-sec">
            <div className='atheader-col'>
                <div className="info-text">
                    <p>➔ "No Data": No trades for this item in the chosen timeframe.</p>
                    <p>➔ If margin is negative, it means high price is lower than low price.</p>
                </div>
                <div className='button-sec'>
                    <div className="timeframe-btn osrs-btn">
                        <button className={timeframe === "latest" ? "active" : ""} onClick={() => handleTimeframeChange("latest")}>Latest Prices</button>
                        <button className={timeframe === "5m" ? "active" : ""} onClick={() => handleTimeframeChange("5m")}>5m</button>
                        <button className={timeframe === "10m" ? "active" : ""} onClick={() => handleTimeframeChange("10m")}>10m</button>
                        <button className={timeframe === "30m" ? "active" : ""} onClick={() => handleTimeframeChange("30m")}>30m</button>
                        <button className={timeframe === "1h" ? "active" : ""} onClick={() => handleTimeframeChange("1h")}>1h</button>
                        <button className={timeframe === "6h" ? "active" : ""} onClick={() => handleTimeframeChange("6h")}>6h</button>
                        <button className={timeframe === "24h" ? "active" : ""} onClick={() => handleTimeframeChange("24h")}>24h</button>
                        <div className="filter-btn osrs-btn">
                          <button onClick={handleFilterButtonClick}>Settings<i className="bi bi-gear-fill"></i></button>
                        </div>
                    </div>
                </div>
            </div>
            {isFilterOpen && (
            <div className="filter-panel">
              <button className="exit-btn" onClick={() => setIsFilterOpen(false)}><i className="bi bi-x"></i></button>
              <div className="above-cont">
                <h3>Settings</h3>
              </div>
              <div className="general-cont">
                <div className="preset-cont">
                <hr></hr>
                  <div className="preset-holder">
                    <div className="presets-title">
                      <i className="bi bi-clipboard-data"></i><h4>Presets</h4>
                    </div>
                    <div className="presets-minimize">
                      <i className="bi bi-caret-down-fill"></i>
                    </div>
                  </div>
                  <div className="presets-checkbox">
                  <ul>
                    <li>
                      <input type="radio" id="f-option" name="selector"/>
                      <label htmlFor="f-option">Show All</label>
                    </li>
                    <li>
                      <input type="radio" id="s-option" name="selector"/>
                      <label htmlFor="s-option">High Margin Flipping</label>
                    </li>
                    <li>
                      <input type="radio" id="t-option" name="selector"/>
                      <label htmlFor="t-option">High Volume Flipping</label>
                    </li>
                  </ul>
                  </div>
                </div>
                <hr></hr>
                <div className="preset-holder">
                    <div className="presets-title">
                      <i className="bi bi-funnel"></i><h4>Filters</h4>
                    </div>
                    <div className="presets-minimize">
                      <i className="bi bi-caret-down-fill"></i>
                    </div>
                </div>
                <div className='inpt-fld'>
                    <div className="input-sec">
                        <div className='input-col'>
                            <h5>Min Buying Price</h5>
                            <input type="number" value={minLowPrice} onChange={(e) => setMinLowPrice(e.target.value)} placeholder="Min Low Price" />
                        </div>
                        <div className="input-col">
                            <h5>High Buying</h5>
                            <input type="number" value={maxLowPrice} onChange={(e) => setMaxLowPrice(e.target.value)} placeholder="Max Low Price" />
                        </div>
                    </div>
                    <div className="input-sec">
                        <div className="input-col">
                            <h5>Minimum Profit</h5>
                            <input type="number" value={minMargin} onChange={(e) => setMinMargin(e.target.value)} placeholder="Minimum Margin" />
                        </div>
                    </div>
                    <div className="input-sec">
                        <div className='input-col'>
                            <h5>Minumum Volume</h5>
                            <input type="number" value={minVolume} onChange={(e) => setMinVolume(e.target.value)} placeholder="Minimum Volume" />
                        </div>
                        <div className='input-col'>
                            <h5>Maximum Volume</h5>
                            <input type="number" value={maxVolume} onChange={(e) => setMaxVolume(e.target.value)} placeholder="Maximum Volume" />
                        </div>
                    </div>
                </div>
                <hr></hr>
                <div className="preset-holder">
                    <div className="presets-title">
                      <i className="bi bi-layout-three-columns"></i><h4>Columns</h4>
                    </div>
                    <div className="presets-minimize">
                      <i className="bi bi-caret-down-fill"></i>
                    </div>
                  </div>
                  <div className="columns-checkbox">
                  <ul>
                    <li>
                      <input type="checkbox" id="f-option" name="selector"/>
                      <label htmlFor="f-option">Select All</label>
                    </li>
                    <li>
                      <input type="checkbox" id="s-option" name="selector"/>
                      <label htmlFor="s-option">Pin</label>
                    </li>
                    <li>
                      <input type="checkbox" id="t-option" name="selector"/>
                      <label htmlFor="t-option">ID</label>
                    </li>
                    <li>
                      <input type="checkbox" id="g-option" name="selector"/>
                      <label htmlFor="g-option">Icon</label>
                    </li>
                    <li>
                      <input type="checkbox" id="h-option" name="selector"/>
                      <label htmlFor="h-option">Name</label>
                    </li>
                    <li>
                      <input type="checkbox" id="k-option" name="selector"/>
                      <label htmlFor="k-option">Buy Price</label>
                    </li>
                    <li>
                      <input type="checkbox" id="l-option" name="selector"/>
                      <label htmlFor="l-option">Sell Price</label>
                    </li>
                    <li>
                      <input type="checkbox" id="m-option" name="selector"/>
                      <label htmlFor="m-option">Profit</label>
                    </li>
                    <li>
                      <input type="checkbox" id="n-option" name="selector"/>
                      <label htmlFor="n-option">ROI</label>
                    </li>
                    <li>
                      <input type="checkbox" id="b-option" name="selector"/>
                      <label htmlFor="b-option">Tax</label>
                    </li>
                    <li>
                      <input type="checkbox" id="v-option" name="selector"/>
                      <label htmlFor="v-option">Limit</label>
                    </li>
                    <li>
                      <input type="checkbox" id="c-option" name="selector"/>
                      <label htmlFor="c-option">Buy Volume</label>
                    </li>
                    <li>
                      <input type="checkbox" id="x-option" name="selector"/>
                      <label htmlFor="x-option">Sell Volume</label>
                    </li>
                    <li>
                      <input type="checkbox" id="z-option" name="selector"/>
                      <label htmlFor="z-option">Average Volume</label>
                    </li>
                  </ul>
                  </div>
                  <hr></hr>
                  <div className="preset-holder">
                    <div className="presets-title">
                      <i className="bi bi-box-seam"></i><h4>Miscellaneous</h4>
                    </div>
                    <div className="presets-minimize">
                      <i className="bi bi-caret-down-fill"></i>
                    </div>
                  </div>
                  <div className="misc-checkbox">
                    <input htmlFor="a-option" type="checkbox" checked={hideNoData} onChange={() => setHideNoData(!hideNoData)}/>
                    <label htmlFor="a-option">Hide items with "No Data"</label>
                  </div>
        </div>
      </div>
            )}
        </div>
        <div className="ge-table">
        <table>
            <thead>
            <tr>
                <th onClick={() => sortByColumn("itemName")}>Item Name</th>
                <th onClick={() => sortByColumn("avgHighPrice")}>Selling Price</th>
                <th onClick={() => sortByColumn("avgLowPrice")}>Buying Price</th>
                <th onClick={() => sortByColumn("margin")}>Profit</th>
                <th onClick={() => sortByColumn("ROI")}>ROI</th>
                <th onClick={() => sortByColumn("highPriceVolume")}>Selling Volume</th>
                <th onClick={() => sortByColumn("lowPriceVolume")}>Buying Volume</th>
            </tr>
            </thead>
            <tbody>
              {formattedPricesData.slice(0, visibleRows).map((item, index) => (
                <tr key={index}>
                  <td>{item.itemName}</td>
                  <td>{item.avgHighPrice}</td>
                  <td>{item.avgLowPrice}</td>
                  <td>{item.margin}</td>
                  <td>{item.ROI}</td>
                  <td>{item.highPriceVolume}</td>
                  <td>{item.lowPriceVolume}</td>
                </tr>
              ))}
            </tbody>
        </table>
      </div>
      {visibleRows < formattedPricesData.length && (
  <button className='button-1' onClick={handleShowMore}>Show More</button>
)}
    </div>
  );
}

export default DataTable;